import { useCallback, Suspense, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Icon, Text } from "@zapier/design-system";
import { Typeahead, type Key } from "@zapier/design-system-beta";
import {
  chatbotAsyncAtomFamily,
  chatbotsAsyncAtom,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { ErrorBoundary } from "../../ErrorBoundary";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import { SelectedItem } from "../SelectedItem";
import styles from "./FloatingChatbotsTypeahead.module.css";

type Props = {
  selectedChatbotId: string | undefined;
};

function ChatbotsTypeaheadLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search chatbots"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <Typeahead.Item key="loading" id="loading">
          Loading chatbots...
        </Typeahead.Item>
      </Typeahead>
    </div>
  );
}

function SelectedChatbotSuspended({ selectedChatbotId }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const selectedChatbot = useAtomValue(
    chatbotAsyncAtomFamily(selectedChatbotId),
  );

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        chatbotId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedChatbot?.name || "Chatbot not found"}
      icon={<Icon name="navAIChatbot" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function ChatbotsTypeaheadSuspended() {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const chatbots = useAtomValue(chatbotsAsyncAtom);

  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          chatbotId: key,
        },
      });

      if (key) {
        // TODO: emitEventOnChatbotConnect
      }
    },
    [takeSnapshot, updateNodeAndSave],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search chatbots"
        placeholder="Search chatbots"
        ref={inputRef}
        onSelectionChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
      >
        {chatbots.map((chatbot) => {
          return (
            <Typeahead.Item
              key={chatbot.id}
              id={chatbot.id}
              textValue={chatbot.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navAIChatbot" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{chatbot.name}</Text>
              </div>
            </Typeahead.Item>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function ChatbotsTypeahead({ selectedChatbotId }: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ChatbotsTypeaheadLoader />}>
        {selectedChatbotId ? (
          <SelectedChatbotSuspended selectedChatbotId={selectedChatbotId} />
        ) : (
          <ChatbotsTypeaheadSuspended />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

export function FloatingChatbotsTypeahead({ selectedChatbotId }: Props) {
  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <ChatbotsTypeahead selectedChatbotId={selectedChatbotId} />
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
